import React, { useMemo, useState } from "react";
import { Check, ExternalLink, LoaderCircle, Search, Sparkles, Upload } from "lucide-react";
import { apiFetch } from "../../commons/axiosReq";
import "./UniversityImporter.css";

export default function UniversityImporter({ countries = [], onChanged }) {
  const [countryId, setCountryId] = useState("");
  const [rankFrom, setRankFrom] = useState(1);
  const [rankTo, setRankTo] = useState(100);
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [savingId, setSavingId] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const countryName = useMemo(() => countries.find((item) => String(item.value) === String(countryId))?.label || "", [countries, countryId]);
  const pendingSuggestions = suggestions.filter((item) => !item.isImported);

  const resetResults = () => {
    setSuggestions([]);
    setMessage("");
  };

  const preview = async () => {
    const from = Number(rankFrom);
    const to = Number(rankTo);
    if (!countryId) {
      setMessageType("error");
      return setMessage("ابتدا کشور را انتخاب کنید.");
    }
    if (!Number.isInteger(from) || !Number.isInteger(to) || from < 1 || to < from) {
      setMessageType("error");
      return setMessage("بازه رتبه معتبر نیست؛ رتبه شروع باید کوچک‌تر یا مساوی رتبه پایان باشد.");
    }
    if (to - from + 1 > 1000) {
      setMessageType("error");
      return setMessage("در هر جستجو حداکثر ۱٬۰۰۰ جایگاه رتبه‌بندی قابل بررسی است.");
    }
    setBusy(true);
    setSuggestions([]);
    setMessage("");
    const result = await apiFetch("UniversityImport/OpenAlex/Preview", "post", {
      countryId: Number(countryId), rankFrom: from, rankTo: to
    });
    setBusy(false);
    if (!result.ok) {
      setMessageType("error");
      return setMessage(result.error || "دریافت دانشگاه‌ها از OpenAlex انجام نشد.");
    }
    const items = Array.isArray(result.data) ? result.data : [];
    setSuggestions(items);
    setMessageType(items.length ? "success" : "info");
    setMessage(items.length
      ? `${items.length.toLocaleString("fa-IR")} دانشگاه از ${countryName} در رتبه‌های ${from.toLocaleString("fa-IR")} تا ${to.toLocaleString("fa-IR")} پیدا شد.`
      : `در این بازه رتبه، دانشگاهی از ${countryName} پیدا نشد.`);
  };

  const approve = async (suggestion, deferRefresh = false) => {
    setSavingId(suggestion.openAlexInstitutionId);
    const result = await apiFetch("UniversityImport/OpenAlex/Approve", "post", {
      countryId: Number(countryId), openAlexInstitutionId: suggestion.openAlexInstitutionId
    });
    setSavingId("");
    if (!result.ok) {
      setMessageType("error");
      setMessage(result.error || `ورود ${suggestion.name} انجام نشد.`);
      return false;
    }
    setSuggestions((current) => current.map((item) => item.openAlexInstitutionId === suggestion.openAlexInstitutionId
      ? { ...item, isImported: true, universityId: result.data?.universityId }
      : item));
    if (!deferRefresh && onChanged) onChanged();
    return true;
  };

  const approveAll = async () => {
    setBulkBusy(true);
    let importedCount = 0;
    for (const suggestion of pendingSuggestions) {
      if (await approve(suggestion, true)) importedCount += 1;
    }
    setBulkBusy(false);
    setSavingId("");
    setMessageType(importedCount === pendingSuggestions.length ? "success" : "error");
    setMessage(importedCount === pendingSuggestions.length
      ? `${importedCount.toLocaleString("fa-IR")} دانشگاه با موفقیت وارد شد.`
      : `${importedCount.toLocaleString("fa-IR")} مورد وارد شد؛ ورود بعضی دانشگاه‌ها کامل نشد.`);
    if (importedCount && onChanged) onChanged();
  };

  return <div className="university-importer">
    <div className="university-importer-intro">
      <span><Sparkles size={21} /></span>
      <div><strong>ورود دانشگاه بر اساس رتبه پژوهشی جهانی</strong><p>رتبه‌ها از ترتیب جهانی دانشگاه‌های آموزشی OpenAlex بر مبنای تعداد استناد محاسبه می‌شوند.</p></div>
    </div>
    <div className="university-importer-controls">
      <label>کشور<select value={countryId} disabled={busy || bulkBusy} onChange={(event) => { setCountryId(event.target.value); resetResults(); }}><option value="">انتخاب کشور</option>{countries.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
      <label>از رتبه<input type="number" min="1" max="10000" value={rankFrom} disabled={busy || bulkBusy} onChange={(event) => { setRankFrom(event.target.value); resetResults(); }} /></label>
      <label>تا رتبه<input type="number" min="1" max="10000" value={rankTo} disabled={busy || bulkBusy} onChange={(event) => { setRankTo(event.target.value); resetResults(); }} /></label>
      <button type="button" disabled={busy || bulkBusy || !countryId} onClick={preview}>{busy ? <LoaderCircle className="university-importer-spinner" size={18} /> : <Search size={18} />}{busy ? "در حال بررسی رتبه‌ها..." : "نمایش دانشگاه‌ها"}</button>
    </div>
    {message && <div className={`university-importer-message is-${messageType}`}>{message}</div>}
    {suggestions.length > 0 && <div className="university-importer-results">
      <div className="university-importer-results-head"><div><strong>نتایج OpenAlex</strong><span>{pendingSuggestions.length.toLocaleString("fa-IR")} مورد قابل ورود</span></div>{pendingSuggestions.length > 1 && <button type="button" disabled={bulkBusy || Boolean(savingId)} onClick={approveAll}>{bulkBusy ? <LoaderCircle className="university-importer-spinner" size={17} /> : <Upload size={17} />}{bulkBusy ? "در حال ورود همه..." : "ورود همه نتایج"}</button>}</div>
      <div className="university-importer-list">{suggestions.map((suggestion) => {
        const saving = savingId === suggestion.openAlexInstitutionId;
        return <article key={suggestion.openAlexInstitutionId} className={suggestion.isImported ? "is-imported" : ""}>
          <div className="university-importer-rank"><small>رتبه جهانی</small><strong>{Number(suggestion.worldRank).toLocaleString("fa-IR")}</strong></div>
          <div className="university-importer-info"><h3>{suggestion.name}</h3><p>{suggestion.cityName || "شهر نامشخص"}، {countryName}</p><div><span>{Number(suggestion.citedByCount || 0).toLocaleString("fa-IR")} استناد</span><span>{Number(suggestion.worksCount || 0).toLocaleString("fa-IR")} اثر</span>{suggestion.rorId && <span>ROR: {suggestion.rorId}</span>}</div></div>
          <a href={suggestion.sourceUrl} target="_blank" rel="noreferrer" aria-label="مشاهده در OpenAlex"><ExternalLink size={17} /></a>
          <button type="button" disabled={suggestion.isImported || saving || bulkBusy || Boolean(savingId)} onClick={() => approve(suggestion)}>{saving ? <LoaderCircle className="university-importer-spinner" size={17} /> : <Check size={17} />}{suggestion.isImported ? "قبلاً وارد شده" : saving ? "در حال ورود..." : "تأیید و ورود"}</button>
        </article>;
      })}</div>
    </div>}
  </div>;
}
